import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";

import { STATS_TIMEFRAME_OPTIONS } from "../../pages/campaigns/constants";
import { get } from "../helpers/HTTP";
import getQueryKeys from "./getQueryKeys";

import type { Metrics, MetricsParameters } from "../types";

async function getMetricsRequest(
  workspaceId: string,
  parameters: MetricsParameters,
  accountIds: string[],
  flowId?: string,
  campaignId?: string,
) {
  const searchParams = new URLSearchParams();

  searchParams.append(
    "timezone",
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  if (parameters.bucketSize) {
    searchParams.append("bucket", parameters.bucketSize);
  }
  if (parameters.from) {
    searchParams.append("from", Math.floor(parameters.from).toString());
  }
  if (parameters.to) {
    searchParams.append("to", Math.floor(parameters.to).toString());
  }
  if (campaignId) {
    searchParams.append("campaign_id", campaignId);
  }
  if (flowId) {
    searchParams.append("flow_id", flowId);
  }
  if (accountIds.length) {
    searchParams.append("account_ids", JSON.stringify(accountIds));
  }
  return (
    await get(
      `workspaces/${workspaceId}/analytics/interactions?${searchParams.toString()}`,
    )
  ).buckets;
}
export default function useMetrics({
  campaignId,
  flowId,
  selectedTimeframe,
  accountIds,
}: {
  campaignId?: string;
  flowId?: string;
  selectedTimeframe: string | MetricsParameters;
  accountIds: string[];
}) {
  const workspace = useSelectedWorkspaceContext();
  const { id: workspaceId, accounts } = workspace;

  const { metricsKeys } = getQueryKeys(workspaceId);

  const statsTimeframeOption = STATS_TIMEFRAME_OPTIONS.find(
    ({ id }) => id === selectedTimeframe,
  );

  const {
    isLoading: isLoadingMetrics,
    data: metrics,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<(Metrics & { timestamp: number })[]>({
    queryKey: metricsKeys.list(
      selectedTimeframe,
      accountIds,
      campaignId,
      flowId,
    ),
    queryFn: () =>
      getMetricsRequest(
        workspaceId,
        typeof selectedTimeframe === "string"
          ? statsTimeframeOption.getParameters(workspace)
          : selectedTimeframe,
        accountIds,
        flowId,
        campaignId,
      ),
    enabled: !!accounts.length,
  });

  // the summed metrics for the provided time period
  let metricsSum: Metrics = {
    accepted_connections: 0,
    replies: 0,
    sent_connections: 0,
    sent_inmails: 0,
    sent_messages: 0,
    views: 0,
    message_requests: 0,
  };

  if (metrics) {
    metricsSum = metrics.reduce((accumulator, bucket) => {
      typedObjectEntries(bucket).forEach(([key, value]) => {
        if (key !== "timestamp") {
          accumulator[key] += value;
        }
      });

      return accumulator;
    }, metricsSum);
  }

  return {
    metrics,
    metricsSum,
    isLoadingMetrics,
    metricsError,
    refetchMetrics,
  };
}
